import React, {useState, useEffect} from 'react';
import { AF_RELATION_PARENT, AzureDevOpsService, ProjectWorkItemData, WorkItemType } from '../services/AzureDevOpsService';
import { PublicClientApplication } from '@azure/msal-browser';
import { useMsal } from '@azure/msal-react';
import { Ticket } from '../services/Ticket';
import { Table, Select } from 'antd';
import './TimeTrackingPage.css'; // Import the CSS file for custom styles

interface TimeTrackingPageProps {
  projectWorkItemData: ProjectWorkItemData[];
  priorityDriverOptions: string[];
}

const TimeTrackingPage: React.FC<TimeTrackingPageProps> = ({ projectWorkItemData, priorityDriverOptions }) => {
  const { instance, accounts } = useMsal();
  const [tickets, setTickets] = useState<any[]>([]);
  const [isLoaded, setIsLoaded] = useState(false);
  const [refresh, setRefresh] = useState<Date | null>()
  const [actionFeedback, setActionFeedback] = useState<string | null>(null); // Added visual feedback for button clicks
  const [filterProject, setFilterProject] = useState<string>("Unity"); // Added state for filtering
  const [filterType, setFilterType] = useState<string | null>(null); // Added state for filtering by ticket type
  const [projects, setProjects] = useState<string[]>([]); // Added state for projects
  const [workItemTypes, setWorkItemTypes] = useState<string[]>([]); // Added state for work item types

  useEffect(() => {
    const savedFilterProject = localStorage.getItem("filterProject");
    const savedFilterType = localStorage.getItem("filterType");

    if (savedFilterProject) {
      setFilterProject(savedFilterProject);
    }

    if (savedFilterType) {
      setFilterType(savedFilterType);
    }
  }, []);

  useEffect(() => {
    const PriorityDrivers = priorityDriverOptions.map(driver => `'${driver}'`).join(", ");
    //TODO: Remove the hardcoded field valued and fetch the from ticket instead
    const wiql = "Select [System.Id] , [System.Title], [System.State] From WorkItems "
      + " Where [Custom.PriorityDriver] not in "+PriorityDrivers+"  AND [System.ChangedDate] >= '2024-12-01' "
      + " AND [System.TeamProject] in "+ filterProject
      + " order by [System.ChangedDate] desc";
    document.title = "Time Tracking";

    const fetchTickets = async () => {
      if (accounts.length > 0) {
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const project = '';
          const result = await devOpsService.fetchTicketsByQuery(organization, project, wiql);

          if (result && Array.isArray(result.workItems) && result.workItems.length > 0) {
            // Parallelize fetching ticket details
            const ticketDetails: Ticket[] = await Promise.all(
              result.workItems.map(async (item: any) => {
                if (item.url) {
                  try {
                    const detail = await devOpsService.fetchWorkItemByUrl(item.url);
                    if (detail && detail.id) {                      
                      return new Ticket(detail)
                    }
                  } catch (err) {
                    console.error("Error fetching:", err);
                  }
                }
                return null;
              })
            );
            // Filter out any nulls (failed fetches)
            setTickets(ticketDetails.filter((t): t is Ticket => (t !== null && t.parent === 0 && t.project !== "Triage")).sort((a, b) => Number(b.updated) - Number(a.updated)));
            setIsLoaded(true);
          } else {
            setTickets([]);
            setIsLoaded(true);
          }
        } catch (error) {
          console.error("Error fetching tickets:", error);
          setTickets([]);
          setIsLoaded(true);
        }
      }
    };
    fetchTickets();
  }, [instance, accounts, refresh, filterProject, priorityDriverOptions]);

  // Define the setParent function to update the parent field
  const setParent = async (ticketId: number, parentId: number) => {
    if (accounts.length > 0) {
      try {
        setActionFeedback(`Updating parent for ticket ID: ${ticketId}...`);
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        const organization = 'sensio';
        

        const result = await devOpsService.createTicketRelation(organization, ticketId, parentId, AF_RELATION_PARENT);

        setRefresh(new Date()); // Trigger a refresh after updating
        if (result) {
          setActionFeedback(`Parent updated successfully for ticket ID: ${ticketId}.`);
        } else {
          setActionFeedback(`Failed to update parent for ticket ID: ${ticketId}.`);
        }
      } catch (error) {
        setActionFeedback(`Error updating parent for ticket ID: ${ticketId}.`);
        console.error(`Error updating parent field for ticket ID: ${ticketId}`, error);
      }
    }
  };

  // Updated the setMaintenance function to use updateTicketFields
  const setMaintenance = async (ticketId: number, Initiative : string) => {
    if (accounts.length > 0) {
      try {
        setActionFeedback(`Updating ticket ID: ${ticketId}...`);
        const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
        const organization = 'sensio';
        const project = '';
        const fields = { 
          "Custom.Initiative" : Initiative,
          "Custom.CapEx" : 'Cost',
          "Custom.GovProject": 'No',
          "Custom.PriorityDriver": 'Maintenance'

         };

        const result = await devOpsService.updateTicketFields(organization, project, ticketId, fields);

        setRefresh(new Date()); // Trigger a refresh after updating
        if (result) {
          setActionFeedback(`Ticket ID: ${ticketId} updated successfully.`);
        } else {
          setActionFeedback(`Failed to update ticket ID: ${ticketId}.`);
        }
      } catch (error) {
        setActionFeedback(`Error updating ticket ID: ${ticketId}.`);
        console.error(`Error updating maintenance field for ticket ID: ${ticketId}`, error);
      }
    }
  };

  const handleFilterProjectChange = (value: string | null) => {
    const filterValue = value || "";
    try {
      localStorage.setItem("filterProject", filterValue);
    } catch (storageError) {
      console.warn('Failed to save filter project setting due to storage quota:', storageError);
    }
    setIsLoaded(false);
    setFilterProject(filterValue);
  };

  const handleFilterTypeChange = (value: string | null) => {
    const filterValue = value || "";
    try {
      localStorage.setItem("filterType", filterValue);
    } catch (storageError) {
      console.warn('Failed to save filter type setting due to storage quota:', storageError);
    }
    setFilterType(filterValue);
  };

  const projectOptions = projects.map((project) => ({ value: project, label: project }));
  
  const getWorkItemTypesForProject = (project: string) => {
    const projectData = projectWorkItemData.find((p: ProjectWorkItemData) => p.project === project);
    return projectData ? projectData.workItemTypes.map((type: WorkItemType) => type.name) : [];
  };
  
  const currentProjectTypes = filterProject ? getWorkItemTypesForProject(filterProject) : [];
  const hasWorkItemData = projectWorkItemData.length > 0;
  
  const typeOptions = [
    { value: "", label: "All" },
    ...currentProjectTypes.map((type: string) => ({ value: type, label: type })),
    ...(currentProjectTypes.length === 0 && !hasWorkItemData ? [
      { value: "Bug", label: "Bug" },
      { value: "Epic", label: "Epic" },
      { value: "Initiative", label: "Initiative" }
    ] : [])
  ];

  const filteredTickets = filterProject
    ? tickets.filter(ticket => ticket.project === filterProject)
    : tickets;

  const filteredTicketsByType = filterType
    ? filteredTickets.filter(ticket => ticket.type === filterType)
    : filteredTickets;

  useEffect(() => {
    const fetchProjects = async () => {
      if (accounts.length > 0) {
        try {
          const devOpsService = new AzureDevOpsService(instance as PublicClientApplication, accounts[0]);
          const organization = 'sensio';
          const result = await devOpsService.fetchProjects(organization);

          if (result && Array.isArray(result.value)) {
            setProjects(result.value.map((project: any) => project.name).sort());
          } else {
            console.error("Failed to fetch projects or no projects found.");
          }
        } catch (error) {
          console.error("Error fetching projects:", error);
        }
      }
    };

    fetchProjects();
  }, [accounts, instance]);

  useEffect(() => {
    if (filterProject && projectWorkItemData.length > 0) {
      const projectData = projectWorkItemData.find((p: ProjectWorkItemData) => p.project === filterProject);
      if (projectData) {
        setWorkItemTypes(projectData.workItemTypes.map((type: WorkItemType) => type.name).sort());
      } else {
        setWorkItemTypes([]);
      }
    }
  }, [filterProject, projectWorkItemData]);

  if (isLoaded) {
    return (
      <div>
        {actionFeedback && <p style={{ color: 'blue' }}>{actionFeedback}</p>}
        <div className="filter-menu">
          <label htmlFor="filter">Filter by Project:</label>
          <Select
            onChange={handleFilterProjectChange}
            value={filterProject || undefined}
            style={{ width: 200 }}
            showSearch
            allowClear
          >
            {projectOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="filter-menu">
          <label htmlFor="filterType">Filter by Ticket Type:</label>
          <Select
            onChange={handleFilterTypeChange}
            value={filterType || undefined}
            style={{ width: 200 }}
            showSearch
            allowClear
          >
            {typeOptions.map((option) => (
              <Select.Option key={option.value} value={option.value}>
                {option.label}
              </Select.Option>
            ))}
          </Select>
          {!hasWorkItemData && (
            <span style={{ fontSize: '12px', color: '#666', fontStyle: 'italic', marginLeft: '10px' }}>
              Loading work item types...
            </span>
          )}
        </div>
        <Table
          dataSource={filteredTicketsByType}
          rowKey={(record: Ticket) => record.id.toString()}
          columns={[
        {
          title: 'ID',
          dataIndex: 'id',
          key: 'id',
          render: (id: string) => (
            <a
          href={`https://dev.azure.com/sensio/Administration/_workitems/edit/${id}`}
          target="_blank"
          rel="noopener noreferrer"
            >
          {id}
            </a>
          ),
        },
        {
          title: 'Title',
          dataIndex: 'title',
          key: 'title',
        },
        {
          title: 'Project',
          dataIndex: 'project',
          key: 'project',
        },
        {
          title: 'Area',
          dataIndex: 'area',
          key: 'area',
        },
        {
          title: 'Actions',
          key: 'actions',
          render: (record: Ticket) => (
            <div className="dropdown">
              <button className="dropdown-button">Actions</button>
              <div className="dropdown-content">
                {(record.project === "Unity" || record.project === "Jira_Unity_migration" || record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "Unity Maintenance")}>Unity Maintenance</button>
                </>}
                {(record.project === "Apps"|| record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "Pocket 8 Maintenance")}>Pocket 8 Maintenance</button> 
                  <button onClick={() => setMaintenance(record.id, "SmartVakt Maintenance")}>SmartVakt Maintenance</button>
                </>}
                {(record.project === "Data" || record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "Data Platform Maintenance")}>Data Platform Maintenance</button>                
                </>}
                {(record.project === "RoomMate" || record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "RoomMate Maintenance")}>RoomMate Maintenance</button>
                </>}
                {(record.project === "Front End" || record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "Vista Maintenance")}>Vista Maintenance</button>
                  {/* Todo: Find a better way to set parents ADO: 24693 */}
                  <button onClick={() => setParent(record.id, 21224)}>Set 21224 as Parent</button>
                </>}
                {(record.project === "Insight"|| record.project === "RnD Cross-Team") && <>
                  <button onClick={() => setMaintenance(record.id, "Insight Maintenance")}>Insight Maintenance</button>
                </>}                
                {record.project === "IKOS" && <>
                  <button onClick={() => setMaintenance(record.id, "IKOS Maintenance")}>IKOS Maintenance</button>
                </>}                

                {record.project === "Team Infrastructure" && <>
                  <button onClick={() => setMaintenance(record.id, "Infrastructure Maintenance")}>Infrastructure Maintenance</button>
                </>}
              </div>
            </div>
          ),
        },
                {
          title: 'Parent',
          dataIndex: 'parent',
          key: 'parent',
        },
        // {
        //   title: 'Updated',
        //   dataIndex: 'updated',
        //   key: 'updated',
        //   render: (updated: string) => new Date(updated).toLocaleString(), // Format the date
        // },
        {
          title: 'Type',
          dataIndex: 'type',
          key: 'type',
        },
        {
          title: 'State',
          dataIndex: 'state',
          key: 'state',
        },
          ] as any}
          pagination={{ pageSize: 10 }}
          size="middle"
        />
      </div>
    );
  }
  else {
    return (
      <div>
        <h1>Loading...</h1>
        <p>Please wait while we fetch the data.</p>
      </div>
    );
  }
};

export default TimeTrackingPage;
